import { Location } from '@angular/common';
import { inject, Injectable, InjectionToken, Provider } from '@angular/core';
import { CanMatchFn, Router } from '@angular/router';
import { ENTITY_PREVIEW_READ_ONLY } from '@processpuzzle/base-entity';
import { DEFINITION_REVISION, DefinitionRevision } from '@processpuzzle/util';

/**
 * The revision of the definitions this page reads, settled once, when the page loads: `draft` inside the designer,
 * `released` everywhere else — see core's `docs/definition-release.md`.
 *
 * **Once per page, and not per navigation**, because what was read is cached at the root: the entity registries, the
 * governed entities, the rule engine, and the injection tokens of the screens built from definitions. Clearing those
 * in place is not reliable, so a page never holds both revisions: crossing between the designer and the application
 * loads the page anew (`pageRevisionGuard`).
 */
export const PAGE_REVISION = new InjectionToken<DefinitionRevision>('PAGE_REVISION');

/** `draft` for the designer's URLs, `released` for every other. */
export function pageRevisionOf(pathname: string, orgKey?: string): DefinitionRevision {
  if (!orgKey) return 'released';
  const designer = `/${orgKey}/design`;
  return pathname === designer || pathname.startsWith(`${designer}/`) ? 'draft' : 'released';
}

/**
 * The page's revision, the readers' revision, and — while the page shows drafts — a read-only Preview: its screens
 * show the organization's live objects through definitions not yet released, and a save through them would write
 * data the running application does not expect.
 */
export function providePageRevision(orgKey?: string, pathname: string = typeof window === 'undefined' ? '/' : window.location.pathname): Provider[] {
  const revision = pageRevisionOf(pathname, orgKey);
  return [
    { provide: PAGE_REVISION, useValue: revision },
    { provide: DEFINITION_REVISION, useValue: () => revision },
    { provide: ENTITY_PREVIEW_READ_ONLY, useValue: revision === 'draft' },
  ];
}

/** Loads a URL as a new page. A service of its own so that tests can stand in for the browser. */
@Injectable({ providedIn: 'root' })
export class PageLoader {
  private readonly location = inject(Location);

  load(url: string): void {
    window.location.assign(this.location.prepareExternalUrl(url));
  }
}

/**
 * Lets a route match only on a page of the given revision. On a page of the other one it loads the requested URL as a
 * new page, and leaves this navigation pending until the browser leaves — not refused, which would let the router
 * fall through to the catch-all and redirect while the page is going anyway.
 */
export function pageRevisionGuard(revision: DefinitionRevision): CanMatchFn {
  return () => {
    if (inject(PAGE_REVISION) === revision) return true;
    const router = inject(Router);
    const navigation = router.getCurrentNavigation();
    inject(PageLoader).load(navigation ? router.serializeUrl(navigation.extractedUrl) : router.url);
    return new Promise<boolean>(() => undefined);
  };
}
